import { AxiosError, AxiosResponse } from 'axios';
import { call, put, takeEvery } from 'redux-saga/effects';
import jwtDecode from 'jwt-decode';
import { AuthService, LoginResponseRequestType } from 'services/auth.service';
import { CandidateService } from 'services/user.service';
import { CompanyService } from 'services/company.service';
import { UserType } from 'types/user';
import { setCookieSessionToken } from 'utils/session';
import { getUserType, requiredCompanyDataCompleted } from 'utils/user';
import { CompanyType } from 'types/company';
import {
  getUserAction,
  setUserAction,
  setIsLoadingAuthAction,
  getCompanyAction
} from './actions';
import {
  AuthSagaActionsType,
  GetUserActionPayloadType,
  LoginActionPayloadType,
  GoogleLoginActionPayloadType,
  FacebookLoginActionPayloadType
} from './types';

import { setIsLoadingAction, setMessageAction } from '../helper/actions';

function* loginSaga(action: LoginActionPayloadType) {
  const { email, code, captchaToken, callback } = action.payload;

  yield put(setIsLoadingAction(true));
  try {
    const response: AxiosResponse<LoginResponseRequestType> = yield call(
      AuthService.codeAuthentication,
      email,
      code,
      captchaToken
    );
    const { data } = response;

    yield call(setCookieSessionToken, data.token);

    yield put(getUserAction(data.token, callback));
    yield put(getCompanyAction(data.token, callback));

    yield put(setMessageAction(null));
  } catch (error) {
    if (error instanceof AxiosError) {
      yield put(setMessageAction({ type: 'error', content: error.message }));
    }
    yield put(setIsLoadingAction(false));
  }
}

function* googleLoginSaga(action: GoogleLoginActionPayloadType) {
  const { accessToken, isCompany, callback } = action.payload;

  yield put(setIsLoadingAction(true));
  try {
    const response: AxiosResponse<LoginResponseRequestType> = yield call(
      AuthService.googleAuthentication,
      {
        accessToken,
        isCompany
      }
    );
    const { data } = response;

    yield call(setCookieSessionToken, data.token);

    yield put(getUserAction(data.token, callback));

    yield put(setMessageAction(null));
  } catch (error) {
    if (error instanceof AxiosError) {
      yield put(setMessageAction({ type: 'error', content: error.message }));
    }
    yield put(setIsLoadingAction(false));
  }
}

function* facebookLoginSaga(action: FacebookLoginActionPayloadType) {
  const { accessToken, isCompany, callback } = action.payload;
  yield put(setIsLoadingAction(true));
  try {
    const response: AxiosResponse<LoginResponseRequestType> = yield call(
      AuthService.facebookAuthentication,
      { accessToken, isCompany }
    );
    const { data } = response;

    yield call(setCookieSessionToken, data.token);

    yield put(getUserAction(data.token, callback));

    yield put(setMessageAction(null));
  } catch (error) {
    if (error instanceof AxiosError) {
      yield put(setMessageAction({ type: 'error', content: error.message }));
    }
    yield put(setIsLoadingAction(false));
  }
}

function* getUserSaga(action: GetUserActionPayloadType) {
  const { token, callback } = action.payload;

  yield put(setIsLoadingAction(true));
  try {
    const { associatedCompanyRoles } =
      jwtDecode<LoginResponseRequestType>(token);
    const hasCnpj = !!associatedCompanyRoles?.length;

    const { data }: AxiosResponse<UserType> = yield call(
      CandidateService.getGeneral
    );

    const userType = getUserType(data.isCompany);

    let dataCompanyDefault: CompanyType = {
      segment: '',
      id: '',
      businessName: '',
      fantasyName: '',
      abstract: '',
      amountIntervalWorkers: '',
      hasAvatar: false,
      hasCover: false,
      cellPhone: '',
      city: '',
      document: '',
      country: '',
      email: '',
      homePhone: '',
      maxMembers: 0,
      sector: '',
      state: '',
      zipCode: '',
      address: '',
      addressNumber: '',
      additionalInfo: '',
      burgh: '',
      finishedRegister: false,
      linkVideo: '',
      descriptionVideo: '',
      socialNetworks: [
        {
          active: false,
          id: '',
          createdAt: '',
          updatedAt: '',
          deletedAt: null,
          name: '',
          link: '',
          companyId: '',
          userId: ''
        }
      ],
      slug: '',
      usageContract: false,
      industry: {
        id: 0,
        name: ''
      },
      jobs: false
    };

    if (hasCnpj) {
      const dataCompany: AxiosResponse<any> = yield call(
        CompanyService.getAssociatedCompany
      );
      dataCompanyDefault = dataCompany?.data[0]?.company;
    }

    yield put(
      setUserAction({
        token,
        userType,
        hasCnpj,
        user: data,
        company: dataCompanyDefault,
        requiredCompanyCompleted: hasCnpj
          ? Boolean(requiredCompanyDataCompleted(dataCompanyDefault))
          : false
      })
    );

    yield put(setMessageAction(null));

    if (callback) yield call(callback);
  } catch (error) {
    if (error instanceof AxiosError) {
      yield put(setMessageAction({ type: 'error', content: error.message }));
    }
  }
  yield put(setIsLoadingAuthAction(false));
  yield put(setIsLoadingAction(false));
}

function* getCompanySaga(action: GetUserActionPayloadType) {
  const { token, callback } = action.payload;

  yield put(setIsLoadingAction(true));
  try {
    const { associatedCompanyRoles } =
      jwtDecode<LoginResponseRequestType>(token);
    const hasCnpj = !!associatedCompanyRoles?.length;

    const { data }: AxiosResponse<UserType> = yield call(
      CandidateService.getGeneral
    );

    const dataCompany: AxiosResponse<any> = yield call(
      CompanyService.getAssociatedCompany
    );

    const userType = getUserType(data.isCompany);

    yield put(
      setUserAction({
        token,
        userType,
        hasCnpj,
        user: data,
        company: dataCompany?.data[0]?.company,
        requiredCompanyCompleted: Boolean(
          requiredCompanyDataCompleted(dataCompany?.data[0]?.company)
        )
      })
    );

    yield put(setMessageAction(null));

    if (callback) yield call(callback);
  } catch (error) {
    if (error instanceof AxiosError) {
      yield put(setMessageAction({ type: 'error', content: error.message }));
    }
  }
  yield put(setIsLoadingAuthAction(false));
  yield put(setIsLoadingAction(false));
}

const authWatcher = [
  takeEvery(AuthSagaActionsType.LOGIN, loginSaga),
  takeEvery(AuthSagaActionsType.GOOGLE_LOGIN, googleLoginSaga),
  takeEvery(AuthSagaActionsType.FACEBOOK_LOGIN, facebookLoginSaga),
  takeEvery(AuthSagaActionsType.GET_USER, getUserSaga),
  takeEvery(AuthSagaActionsType.GET_COMPANY, getCompanySaga)
];
export default authWatcher;
